import { createRouter, createWebHistory, type RouteRecordRaw, type Router } from 'vue-router'

import { useAuthStore } from '@/features/auth/model'

/**
 * Route table and guards (SPEC.md §10.3, §8.1).
 *
 * Every route component is lazy-loaded so each screen ships as its own chunk — §12 caps route
 * chunks at 250 KB gzipped, which one eager bundle would break as soon as the tables and charts
 * arrive.
 *
 * Guards are a convenience for the user, never a security control: the server authorises every
 * endpoint independently (SPEC.md §8.1).
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** Redirect to `/login` when nobody is signed in. */
    requiresAuth?: boolean
    /** Redirect to `/403` unless the signed-in role grants this permission. */
    permission?: string
    /** Reachable while signed out (login, error pages). */
    public?: boolean
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    component: () => import('@/app/layouts/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('@/pages/auth/LoginPage.vue'),
        meta: { public: true },
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/app/layouts/AppShell.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/dashboard/DashboardPage.vue'),
      },
      {
        path: 'change-password',
        name: 'change-password',
        component: () => import('@/pages/auth/ChangePasswordPage.vue'),
      },
      {
        path: 'warehouse/receipts',
        name: 'receipts',
        component: () => import('@/pages/warehouse/ReceiptsPage.vue'),
        meta: { permission: 'receipts.view' },
      },
      {
        path: 'warehouse/receipts/new',
        name: 'receipt-new',
        component: () => import('@/pages/warehouse/ReceiptFormPage.vue'),
        meta: { permission: 'receipts.manage' },
      },
      {
        path: 'warehouse/receipts/:id',
        name: 'receipt-detail',
        component: () => import('@/pages/warehouse/ReceiptDetailPage.vue'),
        meta: { permission: 'receipts.view' },
      },
      {
        path: 'warehouse/receipts/:id/edit',
        name: 'receipt-edit',
        component: () => import('@/pages/warehouse/ReceiptFormPage.vue'),
        meta: { permission: 'receipts.manage' },
      },
      {
        path: 'warehouse/shipments',
        name: 'shipments',
        component: () => import('@/pages/warehouse/ShipmentsPage.vue'),
        meta: { permission: 'shipments.view' },
      },
      {
        path: 'warehouse/shipments/new',
        name: 'shipment-new',
        component: () => import('@/pages/warehouse/ShipmentFormPage.vue'),
        meta: { permission: 'shipments.manage' },
      },
      {
        path: 'warehouse/shipments/:id',
        name: 'shipment-detail',
        component: () => import('@/pages/warehouse/ShipmentDetailPage.vue'),
        meta: { permission: 'shipments.view' },
      },
      {
        path: 'warehouse/shipments/:id/edit',
        name: 'shipment-edit',
        component: () => import('@/pages/warehouse/ShipmentFormPage.vue'),
        meta: { permission: 'shipments.manage' },
      },
      {
        path: 'warehouse/orders',
        name: 'orders',
        component: () => import('@/pages/warehouse/OrdersPage.vue'),
        meta: { permission: 'orders.view' },
      },
      {
        path: 'warehouse/orders/new',
        name: 'order-new',
        component: () => import('@/pages/warehouse/OrderFormPage.vue'),
        meta: { permission: 'orders.create' },
      },
      {
        path: 'warehouse/orders/:id',
        name: 'order-detail',
        component: () => import('@/pages/warehouse/OrderDetailPage.vue'),
        meta: { permission: 'orders.view' },
      },
      {
        path: 'warehouse/orders/:id/edit',
        name: 'order-edit',
        component: () => import('@/pages/warehouse/OrderFormPage.vue'),
        meta: { permission: 'orders.create' },
      },
      {
        path: 'supervision',
        name: 'supervision',
        component: () => import('@/pages/supervision/SupervisionPage.vue'),
        meta: { permission: 'supervision.view' },
      },
      {
        path: 'warehouse/stock',
        name: 'stock',
        component: () => import('@/pages/warehouse/StockPage.vue'),
        meta: { permission: 'stock.view' },
      },
      {
        path: 'warehouse/write-offs',
        name: 'write-offs',
        component: () => import('@/pages/warehouse/WriteOffsPage.vue'),
        meta: { permission: 'stock.view' },
      },
      {
        path: 'warehouse/write-offs/:id',
        name: 'write-off-detail',
        component: () => import('@/pages/warehouse/WriteOffDetailPage.vue'),
        meta: { permission: 'stock.view' },
      },
      {
        path: 'warehouse/returns',
        name: 'returns',
        component: () => import('@/pages/warehouse/ReturnsPage.vue'),
        meta: { permission: 'stock.view' },
      },
      {
        path: 'warehouse/returns/:id',
        name: 'return-detail',
        component: () => import('@/pages/warehouse/ReturnDetailPage.vue'),
        meta: { permission: 'stock.view' },
      },
      {
        // Adjustments are ADMIN-only on the server; the route guard says so too, so a manager is
        // told at the door rather than by a 403 after filling the form in.
        path: 'warehouse/adjustments',
        name: 'adjustments',
        component: () => import('@/pages/warehouse/AdjustmentsPage.vue'),
        meta: { permission: 'users.manage' },
      },
      {
        // `:tab?` — one of the cost categories, `payroll`, or nothing for all of them. In the URL
        // so a kind of cost stays linkable, which is what the totals strip above it links to.
        path: 'expenses/:tab?',
        name: 'expenses',
        component: () => import('@/pages/finance/ExpensesPage.vue'),
        meta: { permission: 'expenses.view' },
      },
      // Payday used to be its own menu. It is a kind of cost, so it is a tab now — and the old
      // address still has to land somewhere, because it was in somebody's bookmarks yesterday.
      { path: 'payroll', redirect: '/expenses/payroll' },
      // Staff had their own screen while the system also had users, and the two lists drifted.
      // One person is one account; payday is where their salary is set.
      { path: 'settings/employees', redirect: '/expenses/payroll' },
      // The company details live in a tab of the settings screen; the old address still works.
      { path: 'settings/company', redirect: '/settings/reference' },
      {
        path: 'settings/products',
        name: 'products',
        component: () => import('@/pages/settings/ProductsPage.vue'),
        meta: { permission: 'settings.view' },
      },
      {
        path: 'settings/products/import',
        name: 'product-import',
        component: () => import('@/pages/settings/ProductImportPage.vue'),
        meta: { permission: 'settings.manage' },
      },
      {
        path: 'settings/products/:id',
        name: 'product-form',
        component: () => import('@/pages/settings/ProductFormPage.vue'),
        meta: { permission: 'settings.manage' },
      },
      {
        path: 'settings/reference',
        name: 'reference',
        component: () => import('@/pages/settings/ReferencePage.vue'),
        meta: { permission: 'settings.view' },
      },
      {
        path: 'settings/suppliers',
        name: 'suppliers',
        component: () => import('@/pages/settings/SuppliersPage.vue'),
        meta: { permission: 'parties.view' },
      },
      {
        path: 'settings/agents',
        name: 'agents',
        component: () => import('@/pages/settings/AgentsPage.vue'),
        meta: { permission: 'parties.view' },
      },
      {
        path: 'settings/stores',
        name: 'stores',
        component: () => import('@/pages/settings/StoresPage.vue'),
        meta: { permission: 'parties.view' },
      },
      {
        path: 'counterparties/suppliers/:id',
        name: 'supplier-detail',
        component: () => import('@/pages/counterparties/SupplierDetailPage.vue'),
        meta: { permission: 'payments.view' },
      },
      {
        path: 'counterparties/agents/:id',
        name: 'agent-detail',
        component: () => import('@/pages/counterparties/AgentDetailPage.vue'),
        meta: { permission: 'payments.view' },
      },
      {
        path: 'counterparties/stores/:id',
        name: 'store-detail',
        component: () => import('@/pages/counterparties/StoreDetailPage.vue'),
        meta: { permission: 'payments.view' },
      },
      {
        path: 'reports/:report?',
        name: 'reports',
        component: () => import('@/pages/reports/ReportsPage.vue'),
        meta: { permission: 'reports.view' },
      },
      {
        path: 'kassa',
        name: 'kassa',
        component: () => import('@/pages/finance/KassaPage.vue'),
        meta: { permission: 'payments.view' },
      },
      {
        path: 'settings/go-live',
        name: 'go-live',
        component: () => import('@/pages/settings/GoLivePage.vue'),
        meta: { permission: 'settings.view' },
      },
      {
        path: 'settings/users',
        name: 'users',
        component: () => import('@/pages/settings/UsersPage.vue'),
        meta: { permission: 'users.manage' },
      },
      {
        path: 'settings/sessions',
        name: 'sessions',
        component: () => import('@/pages/settings/SessionsPage.vue'),
      },
    ],
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/pages/ForbiddenPage.vue'),
    meta: { public: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { public: true },
  },
]

export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    // Returning to a list must restore the reader's place; a new screen always starts at the top.
    scrollBehavior: (_to, _from, savedPosition) => savedPosition ?? { top: 0 },
  })

  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    // One silent refresh attempt on first navigation restores a session across a page reload,
    // because the access token deliberately lives only in memory (SPEC.md §8.1).
    if (!auth.isReady) {
      await auth.bootstrap()
    }

    if (to.meta.requiresAuth && !auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    if (auth.isAuthenticated && to.name === 'login') {
      return { name: 'dashboard' }
    }

    // A password the user was told by someone else is not a password: nothing else is reachable
    // until it is replaced (SPEC.md §8.1, §18).
    if (auth.isAuthenticated && auth.mustChangePassword && to.name !== 'change-password') {
      return { name: 'change-password' }
    }

    if (to.meta.permission && !auth.can(to.meta.permission)) {
      return { name: 'forbidden' }
    }

    return true
  })

  return router
}
